import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import WallProfileModal from '../components/Wall/WallProfileModal';
import FilterPanel from '../components/Discover/FilterPanel';

const styles = {
  container: {
    padding: '16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
  },
  count: {
    fontSize: '13px',
    color: 'var(--text-muted)',
  },
  filterBtn: {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-color)',
    color: 'var(--text-secondary)',
    padding: '6px 14px',
    borderRadius: 'var(--radius-full)',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '4px',
  },
  cell: {
    position: 'relative',
    aspectRatio: '3/4',
    borderRadius: '8px',
    overflow: 'hidden',
    background: 'var(--bg-elevated)',
    cursor: 'pointer',
    transition: 'transform 150ms, box-shadow 150ms',
  },
  cellPhoto: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  cellNoPhoto: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    fontSize: '28px',
    color: 'var(--text-muted)',
    background: 'var(--bg-elevated)',
  },
  cellGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
    pointerEvents: 'none',
  },
  cellInfo: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '6px 8px',
    zIndex: 1,
  },
  cellName: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#fff',
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellMeta: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.7)',
    marginTop: '2px',
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--text-muted)',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
    opacity: 0.4,
  },
};

export default function WallPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // Load saved filters from server on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const res = await api.get('/users/filters');
        setFilters(res.data);
      } catch {
        // use defaults
      } finally {
        setFiltersLoaded(true);
      }
    };
    loadFilters();
  }, []);

  // Build query string from filters
  const buildFilterParams = useCallback((f) => {
    const params = new URLSearchParams();
    if (f.min_age) params.set('min_age', f.min_age);
    if (f.max_age) params.set('max_age', f.max_age);
    if (f.max_distance) params.set('max_distance', f.max_distance);
    if (f.gender_pref?.length) params.set('gender_pref', f.gender_pref.join(','));
    if (f.vaccine_pref?.length) params.set('vaccine_pref', JSON.stringify(f.vaccine_pref));
    return params.toString();
  }, []);

  const fetchWall = useCallback(async (pageNum = 1, append = false, filterOverride = null) => {
    try {
      const f = filterOverride || filters;
      const filterStr = buildFilterParams(f);
      const res = await api.get(`/discover/wall?page=${pageNum}&limit=99${filterStr ? '&' + filterStr : ''}`);
      if (append) {
        setUsers((prev) => [...prev, ...res.data.users]);
      } else {
        setUsers(res.data.users);
      }
      setTotal(res.data.total);
      setPages(res.data.pages);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to load wall:', err);
    }
  }, [filters, buildFilterParams]);

  // Fetch wall when filters are loaded or changed
  useEffect(() => {
    if (!filtersLoaded) return;
    setLoading(true);
    fetchWall(1, false, filters).finally(() => setLoading(false));
  }, [filtersLoaded, filters]);

  const handleLoadMore = async () => {
    if (page >= pages || loadingMore) return;
    setLoadingMore(true);
    await fetchWall(page + 1, true);
    setLoadingMore(false);
  };

  const handleFiltersApply = (newFilters) => {
    setFilters(newFilters);
    // fetchWall will be called via the useEffect above
  };

  const handleProfileClose = () => {
    setSelectedUserId(null);
    fetchWall(1, false, filters);
  };

  const hasActiveFilters =
    filters.gender_pref?.length > 0 ||
    filters.vaccine_pref?.length > 0 ||
    (filters.min_age && filters.min_age !== 18) ||
    (filters.max_age && filters.max_age !== 50) ||
    (filters.max_distance && filters.max_distance !== 50);

  if (loading) {
    return (
      <div className="loading-spinner" style={{ height: '50vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <h2 style={styles.title}>The Wall</h2>
          <span style={styles.count}>{total} people{hasActiveFilters ? ' (filtered)' : ' nearby'}</span>
        </div>
        <button
          style={{
            ...styles.filterBtn,
            ...(hasActiveFilters
              ? { borderColor: 'var(--accent)', color: 'var(--accent)' }
              : {}),
          }}
          onClick={() => setShowFilters(true)}
        >
          Filters {hasActiveFilters ? '●' : ''}
        </button>
      </div>

      {users.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>🧱</div>
          <h3 style={{ marginBottom: '8px' }}>No matches found</h3>
          <p style={{ fontSize: '14px' }}>
            {hasActiveFilters
              ? 'Try adjusting your filters to see more people.'
              : 'No other users on the platform yet.'}
          </p>
          {hasActiveFilters && (
            <button
              className="btn btn-secondary"
              style={{ marginTop: '12px' }}
              onClick={() => setShowFilters(true)}
            >
              Adjust filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={styles.grid}>
            {users.map((user) => (
              <div
                key={user.id}
                style={styles.cell}
                onClick={() => setSelectedUserId(user.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.03)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  e.currentTarget.style.zIndex = '2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '';
                  e.currentTarget.style.zIndex = '';
                }}
              >
                {user.photo ? (
                  <img
                    src={`/uploads/${user.photo}`}
                    alt={user.display_name}
                    style={styles.cellPhoto}
                    loading="lazy"
                  />
                ) : (
                  <div style={styles.cellNoPhoto}>
                    {user.display_name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <div style={styles.cellGradient} />
                <div style={styles.cellInfo}>
                  <div style={styles.cellName}>
                    {user.display_name}{user.age ? `, ${user.age}` : ''}
                  </div>
                  <div style={styles.cellMeta}>
                    {user.distance != null ? `${user.distance} mi` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {page < pages && (
            <div style={styles.loadMore}>
              <button
                className="btn btn-secondary"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : `Load more (${total - users.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}

      {selectedUserId && (
        <WallProfileModal
          userId={selectedUserId}
          onClose={handleProfileClose}
        />
      )}

      {showFilters && (
        <FilterPanel
          filters={filters}
          onApply={handleFiltersApply}
          onClose={() => setShowFilters(false)}
        />
      )}
    </div>
  );
}
